/**
 * Shared vocabulary for where a cropped image keeps its focus. Values are the
 * CSS object-position / background-position keywords, applied verbatim — no
 * translation layer to drift.
 */
type ImagePosition = "center" | "top" | "bottom" | "left" | "right";

const IMAGE_POSITION_OPTIONS = [
    { label: "Center", value: "center" },
    { label: "Top", value: "top" },
    { label: "Bottom", value: "bottom" },
    { label: "Left", value: "left" },
    { label: "Right", value: "right" },
  ] as const,

  /** Inline style for an <img> with object-cover; undefined when center (the CSS default). */
  imagePositionStyle = (
    position: string | undefined,
  ): { objectPosition: string } | undefined => {
    if (!position || position === "center") {
      return undefined;
    }
    return { objectPosition: position };
  };

/**
 * Lets the browser pick a photo's width, so a phone never downloads a desktop file. Applies only
 * to photos stored in Cloudflare Images; any other URL (an upload preview, a stock link) passes
 * through unchanged.
 */
type ImageSlot = "full_width" | "half_width" | "grid_tile" | "thumbnail" | "lightbox";

/** Cloudflare's recommended maximums (640 phone, 960 tablet, 1920 desktop), plus a laptop step. */
const IMAGE_WIDTHS = ["640", "960", "1280", "1920"] as const,
  /**
   * The phone values understate the rendered width on purpose. A browser multiplies `sizes` by the
   * screen density, so an honest 100vw sends a 3x phone the desktop file; 213px caps it at the 640 width. Keep them only on images
   * whose CSS sets the width: `sizes` also becomes the image's intrinsic width. Lightbox has no
   * CSS width, so it stays honest.
   */
  IMAGE_SLOT_SIZES: Record<ImageSlot, string> = {
    full_width: "(max-width: 640px) 213px, 100vw",
    half_width: "(max-width: 767px) 213px, 50vw",
    grid_tile: "(max-width: 767px) 213px, 33vw",
    thumbnail: "96px",
    lightbox: "100vw",
  },
  CLOUDFLARE_PUBLIC_IMAGE = /^(?<base>https:\/\/imagedelivery\.net\/[^/]+\/[^/]+)\/public$/;

/**
 * Props for an `<img>` in the given slot. `sizes` and `srcSet` come first because a browser may
 * start fetching as soon as `src` is set.
 */
const responsiveImageProps = (
  src: string | undefined,
  slot: ImageSlot,
  position?: string,
): { sizes?: string; srcSet?: string; src: string | undefined } => {
  const base = CLOUDFLARE_PUBLIC_IMAGE.exec(src ?? "")?.groups?.base;
  if (base === undefined) {
    return { src };
  }
  const gravity = position && position !== "center" ? `,gravity=${position}` : "",
    // A full-width photo fills a tall phone screen, so a wide one scaled to 640 would stretch.
    phoneFile = slot === "full_width" ? `w=640,h=960,fit=cover${gravity}` : "w=640,fit=scale-down",
    [, ...largerWidths] = IMAGE_WIDTHS,
    largerFiles = largerWidths.map((width) => `${base}/w=${width},fit=scale-down ${width}w`);
  return {
    sizes: IMAGE_SLOT_SIZES[slot],
    srcSet: [`${base}/${phoneFile} 640w`, ...largerFiles].join(", "),
    src,
  };
};

export type { ImagePosition, ImageSlot };
export { IMAGE_POSITION_OPTIONS, imagePositionStyle, responsiveImageProps };
